/**
 * Piezas de vidrio y productos con detalles (respuestas de la dueña del 2026-10-15; enmienda de la spec 06).
 * Reglas que usan la API y la pantalla por igual: que las piezas declaradas quepan en la lámina partida, el nombre
 * de cada pieza y los dos artículos de la lista del punto de venta.
 */
import { Money } from './money/index.ts';

/** Los dos artículos del punto de venta: al buscarlos se despliega la lista completa (su «opción 2»). */
export const ARTICULO_PIEZAS_VIDRIO = 'PIEZAS DE VIDRIOS';
export const ARTICULO_PRODUCTOS_DETALLE = 'PRODUCTOS CON DETALLES';

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * ¿La búsqueda del punto de venta pide uno de los dos artículos? Basta con el comienzo del nombre (5 letras o más,
 * para no tapar productos que empiezan igual): «piezas», «PIEZAS DE VID», «produc»… Sin acentos ni mayúsculas.
 */
export function articuloEspecialDe(q: string): 'vidrio' | 'detalle' | null {
  const b = normalizar(q);
  if (b.length < 5) return null;
  if (ARTICULO_PIEZAS_VIDRIO.startsWith(b)) return 'vidrio';
  if (ARTICULO_PRODUCTOS_DETALLE.startsWith(b)) return 'detalle';
  return null;
}

/** Medida en milímetros sin ceros de más: 1400 · 1400,5. */
export function mm(v: string | Money): string {
  const m = typeof v === 'string' ? Money.parse(v) : v;
  const [entero = '0', decimales = ''] = m.toDocumento().split('.');
  const resto = decimales.replace(/0+$/, '');
  return resto ? `${entero},${resto}` : entero;
}

/** «1400x3660» (como la escribe la dueña). */
export const medidaMm = (alto: string | Money, ancho: string | Money) => `${mm(alto)}x${mm(ancho)}`;

/** Área en m² de una medida en milímetros. */
export const areaM2 = (alto: string | Money, ancho: string | Money) => Money.parse(alto).mul(ancho).div(1_000_000);

/** ¿Cabe la pieza en la lámina? Se puede girar: 2440x1000 cabe en una de 3660x2440. */
export function cabeEnLamina(pieza: { alto_mm: string; ancho_mm: string }, lamina: { largo_mm: number | string; ancho_mm: number | string }): boolean {
  const [a, b] = [Money.parse(pieza.alto_mm), Money.parse(pieza.ancho_mm)];
  const [l, w] = [Money.parse(String(lamina.largo_mm)), Money.parse(String(lamina.ancho_mm))];
  return (a.lte(l) && b.lte(w)) || (a.lte(w) && b.lte(l));
}

export interface RevisionLamina {
  /** m² que ocupan las piezas declaradas (cada medida por su cantidad). */
  usado_m2: Money;
  /** m² de la lámina partida (por las láminas reportadas: 0,5 es media). */
  lamina_m2: Money;
  /** Índices de las piezas que no caben en la lámina ni giradas. */
  no_caben: number[];
  /** Las piezas ocupan más que la lámina. */
  excede: boolean;
}

/**
 * Las piezas declaradas tienen que corresponder a la lámina reportada (respuesta de la dueña del 2026-10-15): cada
 * una cabe en la lámina y entre todas no ocupan más que ella. Si no, el reporte no se envía hasta corregir las medidas.
 */
export function revisarPiezasEnLamina(piezas: { alto_mm: string; ancho_mm: string; cantidad: string }[], lamina: { largo_mm: number | string; ancho_mm: number | string }, laminas: string): RevisionLamina {
  const usado = Money.sumar(piezas.map((z) => areaM2(z.alto_mm, z.ancho_mm).mul(z.cantidad)));
  const total = areaM2(String(lamina.largo_mm), String(lamina.ancho_mm)).mul(laminas);
  const no_caben = piezas.flatMap((z, i) => (cabeEnLamina(z, lamina) ? [] : [i]));
  return { usado_m2: usado, lamina_m2: total, no_caben, excede: usado.gt(total) };
}

/** «12,34 m²» */
export const m2 = (v: Money) => `${v.redondear2().toDisplay()} m²`;

/** Medida y espesor en el nombre de la lámina: «VIDRIO CLARO 3*3660*2440», «VIDRIO BRONCE 6*3300X2400». */
const MEDIDA_EN_NOMBRE = /\s*\d+(?:[.,]\d+)?\s*[*xX]\s*\d{3,5}\s*[*xX]\s*\d{3,5}/;

/**
 * Nombre de la pieza para la venta, como lo escribe la dueña: «Vidrio claro 3mm 1400x3660». Sale del nombre de la
 * lámina sin su medida (el color y el tipo: «VIDRIO REFLECTIVO 5*3300*2440 VERDE» → «Vidrio reflectivo verde»), el
 * espesor de la ficha y la medida de la pieza. La dueña lo puede cambiar al aprobar.
 */
export function nombrePiezaVidrio(laminaNombre: string, espesorMm: string | null, alto: string, ancho: string): string {
  const base = laminaNombre.replace(MEDIDA_EN_NOMBRE, ' ').replace(/\s+/g, ' ').trim();
  const frase = base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : 'Pieza de vidrio';
  return [frase, espesorMm ? `${mm(espesorMm)}mm` : null, medidaMm(alto, ancho)].filter(Boolean).join(' ');
}
